import type { FishGroupDetail, UpdateFishGroupMetadataRequest } from '@/domain/fishGroups';

export interface MetadataFormValues {
    name: string;
    notes: string;
    /** Held as a string so an emptied input stays '' rather than becoming NaN. */
    initialPopulationSize: string;
    reason: string;
}

/**
 * Which fields react-hook-form considers dirty. Only the shape we need — RHF's
 * own `dirtyFields` type is a deep partial that is awkward to pass around.
 */
export interface MetadataDirtyFields {
    name?: boolean;
    notes?: boolean;
    initialPopulationSize?: boolean;
}

export function toMetadataFormValues(detail: FishGroupDetail): MetadataFormValues {
    return {
        name: detail.name,
        notes: detail.notes ?? '',
        initialPopulationSize:
            detail.initialPopulationSize == null ? '' : String(detail.initialPopulationSize),
        // Never pre-filled: the reason describes this correction, not the last one.
        reason: '',
    };
}

/**
 * True when at least one *stored* field was edited.
 *
 * `reason` is excluded deliberately: typing only a reason changes no stored
 * value, so the backend would treat it as a no-op. Gating the submit button on
 * this keeps it honest about whether there is anything to save.
 */
export function hasMetadataFieldChanges(dirtyFields: MetadataDirtyFields): boolean {
    return Boolean(dirtyFields.name || dirtyFields.notes || dirtyFields.initialPopulationSize);
}

/**
 * Build the PATCH body from the form state.
 *
 * Only dirty fields are included, because the endpoint distinguishes three
 * cases per field and a full snapshot would collapse them:
 *
 *   - absent  → leave the stored value untouched
 *   - null    → clear the stored value
 *   - a value → set it
 *
 * Sending every field on every save would journal an audit entry naming fields
 * the operator never touched, which is exactly what the changelog must not do.
 */
export function buildMetadataRequestBody(
    values: MetadataFormValues,
    dirtyFields: MetadataDirtyFields
): UpdateFishGroupMetadataRequest {
    const notes = values.notes.trim();
    const population = values.initialPopulationSize.trim();

    return {
        reason: values.reason.trim(),
        ...(dirtyFields.name ? { name: values.name.trim() } : {}),
        // An emptied textarea means "clear it", not "set it to an empty string".
        ...(dirtyFields.notes ? { notes: notes === '' ? null : notes } : {}),
        ...(dirtyFields.initialPopulationSize
            ? { initialPopulationSize: population === '' ? null : Number(population) }
            : {}),
    };
}
